import React from 'react';
import './style.scss';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { I18n } from 'react-redux-i18n';
import { Select, MenuItem, Button } from '@material-ui/core';
import Arrow from '@material-ui/icons/KeyboardArrowDown';
import Alert from '../../../../Components/Alert';
import CategoryList from './CategoryList';
import ProductList from './ProductList';
import ConfiguredRecap from './ConfiguredRecap';
import BindingModal from './BindingModal';
import * as menuActions from '../../../../Containers/Partners/Components/ProductsSelector/service/menus/actions';
import * as categoryActions from '../../../../Containers/Partners/Components/ProductsSelector/service/categories/actions';
import { ALL } from '../../../../Containers/Partners/Components/ProductsSelector/Components/ProductsSelectorModal/utils';
import { confirm } from '../../../../utils';
import {
	getMenusList,
	getMenuSelected,
	getLoaded,
	getFlatProducts,
	getConfiguredByProduct,
} from './selector';

// Navigateur produits pour l'association aux créneaux (page globale, produit only).
// Réutilise les données productsSelector (déjà chargées) ; la config d'un produit
// se fait dans la popup (BindingModal) et s'écrit directement via saveTimeslotRules.
class TimeslotBinding extends React.Component {
	// openProductId : produit ouvert en config simple (clic sur une ligne)
	// selectionMode : mode « sélection multiple » (switch dans la colonne produits)
	// selectedIds : ensemble des produits cochés en mode sélection ({ [id]: true })
	// multiOpen : popup de config ouverte pour la sélection multiple
	state = {
		openProductId: null,
		selectionMode: false,
		selectedIds: {},
		multiOpen: false,
	};

	onChangeMenu = e => {
		const value = e.target.value;
		if (value && value !== this.props.menuSelected) {
			this.props.selectMenu(value);
			// on repart sur « Toutes » : une catégorie d'un autre menu n'a pas de sens
			this.props.selectCategory(ALL);
		}
	};

	openProduct = id => this.setState({ openProductId: String(id) });

	closeModal = () => this.setState({ openProductId: null });

	// Bascule le mode sélection ; en le désactivant on repart d'une sélection vide.
	toggleSelectionMode = () =>
		this.setState(prevState => ({
			selectionMode: !prevState.selectionMode,
			selectedIds: {},
		}));

	// Coche/décoche un produit en mode sélection.
	toggleSelect = id => {
		const key = String(id);
		this.setState(prevState => {
			const selectedIds = { ...prevState.selectedIds };
			if (selectedIds[key]) {
				delete selectedIds[key];
			} else {
				selectedIds[key] = true;
			}
			return { selectedIds };
		});
	};

	// Ouvre la config multi. Si des produits sélectionnés ont déjà une configuration,
	// on prévient AVANT d'ouvrir (l'écrasement est confirmé ici, pas après édition).
	openMultiConfig = () => {
		const { configuredByProduct, flatProducts } = this.props;
		const configuredNames = Object.keys(this.state.selectedIds)
			.filter(id => configuredByProduct[String(id)])
			.map(id => {
				const p = flatProducts[String(id)];
				return p ? p.name : `#${id}`;
			});

		if (!configuredNames.length) {
			this.setState({ multiOpen: true });
			return;
		}

		const shown = configuredNames.slice(0, 5);
		const namesMd =
			shown.map(n => `- ${n}`).join('\n') +
			(configuredNames.length > shown.length
				? `\n- ${I18n.t('settings.binding.overwrite_more', {
						count: configuredNames.length - shown.length,
				  })}`
				: '');
		confirm(
			I18n.t('settings.binding.overwrite_confirm', {
				count: configuredNames.length,
				names: namesMd,
			})
		).then(
			() => this.setState({ multiOpen: true }),
			() => {}
		);
	};

	// Ferme la popup multi ; à la sauvegarde on vide aussi la sélection et on sort du mode.
	closeMultiModal = (saved = false) =>
		this.setState(prevState => ({
			multiOpen: false,
			selectionMode: saved ? false : prevState.selectionMode,
			selectedIds: saved ? {} : prevState.selectedIds,
		}));

	render() {
		const { loaded, enabled, timeslots, menus, menuSelected, flatProducts } =
			this.props;
		const { openProductId, selectionMode, selectedIds, multiOpen } = this.state;
		const selectedProductIds = Object.keys(selectedIds);

		if (!loaded) {
			return <p className="binding-empty">{I18n.t('general.loading')}</p>;
		}

		// sans créneaux activés/définis, l'association n'a pas de sens : on garde
		// le composant visible mais flouté et non interactif, avec l'alerte par-dessus.
		const disabled = !enabled || !timeslots.length;
		const openProduct = openProductId ? flatProducts[openProductId] : null;
		// un seul menu : inutile d'afficher le sélecteur, il est déjà pris en compte
		// (le reducer initialise `selected` sur le premier menu)
		const hasMultipleMenus = Object.keys(menus).length > 1;

		return (
			<div className="timeslot-binding-page">
				{!disabled ? (
					<Alert type="info">{I18n.t('settings.binding.intro')}</Alert>
				) : null}

				<div className="binding-stage">
					<div
						className={
							'binding-stage-content' +
							(disabled ? ' binding-stage-blurred' : '')
						}
						aria-hidden={disabled}>
						{hasMultipleMenus ? (
							<div className="binding-menu-selector">
								<span className="binding-menu-label">
									{I18n.t('settings.binding.menu')}
								</span>
								<Select
									value={menuSelected || ''}
									onChange={this.onChangeMenu}
									IconComponent={Arrow}>
									{Object.values(menus).map(menu => (
										<MenuItem key={menu.id} value={menu.id}>
											{menu.name}
										</MenuItem>
									))}
								</Select>
							</div>
						) : null}

						<div className="binding-columns">
							<div className="binding-col binding-col-category">
								<CategoryList />
							</div>
							<div className="binding-col binding-col-products">
								<ProductList
									onOpenProduct={this.openProduct}
									selectionMode={selectionMode}
									selectedIds={selectedIds}
									onToggleSelectionMode={this.toggleSelectionMode}
									onToggleSelect={this.toggleSelect}
									onConfigureSelection={this.openMultiConfig}
								/>
							</div>
							<div className="binding-col binding-col-recap">
								<ConfiguredRecap onOpenProduct={this.openProduct} />
							</div>
						</div>
					</div>

					{disabled ? (
						<div className="binding-overlay">
							<div className="binding-overlay-inner">
								<Alert type="info">
									{I18n.t('settings.binding.disabled_state')}
								</Alert>
								{this.props.onGoToDefinition ? (
									<div className="binding-overlay-action">
										<Button
											color="primary"
											variant="contained"
											onClick={this.props.onGoToDefinition}>
											{I18n.t('settings.timeslots.tab_definition')}
										</Button>
									</div>
								) : null}
							</div>
						</div>
					) : null}
				</div>

				{!disabled ? (
					<BindingModal
						open={!!openProduct}
						product={openProduct}
						onClose={this.closeModal}
					/>
				) : null}

				{!disabled ? (
					<BindingModal
						open={multiOpen}
						multi
						productIds={selectedProductIds}
						onClose={() => this.closeMultiModal(false)}
						onSaved={() => this.closeMultiModal(true)}
					/>
				) : null}
			</div>
		);
	}
}

TimeslotBinding.propTypes = {
	// remonte au sous-onglet « Définir les créneaux » (raccourci de l'overlay)
	onGoToDefinition: PropTypes.func,
};

const mapStateToProps = state => {
	const timeslot = (state.service && state.service.timeslot) || {};
	return {
		loaded: getLoaded(state),
		enabled: timeslot.enabled || false,
		timeslots: timeslot.timeslots || [],
		menus: getMenusList(state),
		menuSelected: getMenuSelected(state),
		flatProducts: getFlatProducts(state),
		configuredByProduct: getConfiguredByProduct(state),
	};
};

const mapDispatchToProps = dispatch => ({
	selectMenu: bindActionCreators(menuActions.select, dispatch),
	selectCategory: bindActionCreators(categoryActions.select, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(TimeslotBinding);
